import { Table } from 'antd';
import { BellOff, SearchX, ShieldAlert } from 'lucide-react';
import { Navigate } from 'react-router';
import { EmptyState } from '../../../shared/components/EmptyState';
import { ListSearch } from '../../../shared/components/ListSearch';
import { TableSkeleton } from '../../../shared/components/TableSkeleton';
import { useRaisedAlerts } from '../hooks/use-raised-alerts';
import { isAnyFlagFilterOn } from '../policies/alert-quick-filters';
import { AlertFeedStatus } from './AlertFeedStatus';
import { AlertQuickFilters } from './AlertQuickFilters';
import { raisedAlertColumns } from './raised-alert-columns';
import { ResolveAlertModal } from './ResolveAlertModal';
import type { AlertView } from '../alerts.api';

/** The alert inbox: raised alerts to work from, newest first, kept current as they change. Open ones can be acknowledged, any unresolved one resolved or handed to someone else. */
export function RaisedAlertsTab() {
  const inbox = useRaisedAlerts();
  const { state, list, isRefreshing, connection, now, resolving, resolve, closeResolving } = inbox;

  if (state.status === 'forbidden') return <Navigate to="/" replace />;
  if (state.status === 'error')
    return <EmptyState icon={ShieldAlert} title="Couldn't load alerts" description="Something went wrong loading the alerts. Try reloading the page." />;
  if (state.status === 'loading') return <TableSkeleton label="Loading alerts" withSearch />;

  const emptyText =
    list.isFiltered || isAnyFlagFilterOn(list.query) ? (
      <EmptyState icon={SearchX} title="No matching alerts" description="No alerts match this search or these filters." />
    ) : (
      <EmptyState icon={BellOff} title="No alerts raised" description="When a rule's condition is met on a truck, the alert appears here." />
    );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <ListSearch value={list.query.q} placeholder="Search registration or rule" onSearch={(q) => list.setQuery({ q }, { replace: true })} />
        <AlertQuickFilters list={list} />
        <AlertFeedStatus connection={connection} />
      </div>
      <Table<AlertView>
        rowKey="id"
        dataSource={state.page.items}
        columns={raisedAlertColumns({
          list,
          now,
          canRespond: inbox.canRespond,
          canAssign: inbox.canAssign,
          answeringId: inbox.answeringId,
          onAnswer: inbox.answer,
          onReassign: inbox.reassign,
        })}
        loading={isRefreshing}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText }}
        {...list.tableProps<AlertView>(state.page.total)}
      />
      <ResolveAlertModal alert={resolving} onResolve={resolve} onClose={closeResolving} />
    </div>
  );
}
